import { HttpApiService } from "@/shared/services/httpApi.service";

import type {
  CreateOtcSellOrderPayload,
  CreateOtcSellQuotePayload,
  OtcSellOrder,
  OtcSellOrdersPage,
  OtcSellOrdersQuery,
  OtcSellPair,
  OtcSellQuote,
} from "../types/cryptoSell.types";
import type { OtcVolumeDiscount, OtcVolumeDiscountPayload } from "../types/cryptoSwaps.types";
import { buildQuery, normalizeOtcVolumeDiscount, readOtcPage } from "./cryptoSwaps.service";

const BASE_URL = "/internal/otc/sell";

/** Same guard as the purchase: StrictMode double-fires, and a concurrent duplicate would read as a 409. */
const inFlightSaleCreations = new Map<string, Promise<OtcSellOrder>>();

export function normalizeOtcSellPairs(payload: unknown): OtcSellPair[] {
  const source = (payload as { pairs?: unknown } | null | undefined)?.pairs;
  if (!Array.isArray(source)) return [];
  return source.filter(
    (candidate): candidate is OtcSellPair =>
      !!candidate &&
      typeof candidate === "object" &&
      typeof (candidate as OtcSellPair).pair === "string" &&
      typeof (candidate as OtcSellPair).send_currency === "string" &&
      typeof (candidate as OtcSellPair).receive_currency === "string" &&
      Array.isArray((candidate as OtcSellPair).networks),
  );
}

export const CryptoSellService = {
  async listPairs(organizationId?: string | null): Promise<OtcSellPair[]> {
    const { data } = await HttpApiService.get<unknown>(
      `${BASE_URL}/pairs${buildQuery({ organization_id: organizationId })}`,
    );
    return normalizeOtcSellPairs(data);
  },

  async createQuote(payload: CreateOtcSellQuotePayload, signal?: AbortSignal): Promise<OtcSellQuote> {
    const { data } = await HttpApiService.post<OtcSellQuote>(`${BASE_URL}/quotes`, payload, { signal });
    if (!data?.id) throw new Error("otc: the sell quote response carries no id");
    return data;
  },

  async volumeDiscount(
    quoteId: string,
    payload: OtcVolumeDiscountPayload,
    signal?: AbortSignal,
  ): Promise<OtcVolumeDiscount | null> {
    const { data } = await HttpApiService.post<unknown>(
      `${BASE_URL}/quotes/${quoteId}/volume-discount`,
      payload,
      { signal },
    );
    return normalizeOtcVolumeDiscount(data);
  },

  /** Never call twice for one attempt: the quote is single-use. Use {@link createOrderOnce}. */
  async createOrder(payload: CreateOtcSellOrderPayload): Promise<OtcSellOrder> {
    const { data } = await HttpApiService.post<OtcSellOrder>(`${BASE_URL}/orders`, payload, {
      headers: { "Idempotency-Key": payload.idempotency_key },
    });
    return data;
  },

  /** One creation per idempotency key. A failed attempt stays retryable with the same key. */
  createOrderOnce(payload: CreateOtcSellOrderPayload): Promise<OtcSellOrder> {
    const key = payload.idempotency_key;
    const existing = inFlightSaleCreations.get(key);
    if (existing) return existing;

    const request = CryptoSellService.createOrder(payload).finally(() => {
      inFlightSaleCreations.delete(key);
    });
    inFlightSaleCreations.set(key, request);
    return request;
  },

  async getOrder(saleId: string, signal?: AbortSignal): Promise<OtcSellOrder> {
    const { data } = await HttpApiService.get<OtcSellOrder>(
      `${BASE_URL}/orders/${encodeURIComponent(saleId)}`,
      {
        signal,
      },
    );
    return data;
  },

  async listOrders(query: OtcSellOrdersQuery, signal?: AbortSignal): Promise<OtcSellOrdersPage> {
    const { data } = await HttpApiService.get<unknown>(`${BASE_URL}/orders${buildQuery({ ...query })}`, {
      signal,
    });
    return readOtcPage<OtcSellOrder>(data, query);
  },
};
